<script setup>
import PageHero from '@/views/front-pages/PageHero.vue'
import PublicPageShell from '@/views/front-pages/PublicPageShell.vue'
import { useApi } from '@/utils/api'
import { useConfigStore } from '@core/stores/config'

definePage({ meta: { layout: 'blank', public: true } })

const store = useConfigStore()

store.skin = 'default'

// Addendum 15, Modul BT §6 poin 3: ilustrasi/hero OPSIONAL, diupload admin
// lewat /platform > SEO Halaman Publik (reuse og_image_path, lihat
// SeoSettingService::heroImageUrl()) -- kalau belum diupload, heroImageUrl
// tetap null dan halaman ini tampil PERSIS seperti sebelum modul ini ada
// (tanpa gambar), tidak ada regresi.
const heroImageUrl = ref(null)

const fetchHeroImage = async () => {
  try {
    const response = await useApi('/api/public/page-hero-image', { query: { path: '/kontak' } }).get()
    const body = response?.data ?? response

    heroImageUrl.value = body?.image_url ?? null
  } catch (err) {
    console.error('❌ Gagal memuat gambar ilustrasi halaman:', err)
  }
}

onMounted(fetchHeroImage)

// Kontak sungguhan yang sudah dipakai di pages/support.vue (halaman bantuan
// in-app) -- dipertahankan sama persis di sini, BUKAN nomor/email karangan
// baru. Belum ada endpoint backend untuk form kontak publik, jadi form di
// bawah membuka WhatsApp dengan pesan sudah terisi (sama pola dengan
// support.vue), bukan form yang "submit" ke tempat yang tidak ada.
const WHATSAPP_NUMBER = '6283895562263'
const SUPPORT_EMAIL = 'support@pintukasir.com'

const contactMethods = [
  {
    icon: 'ri-whatsapp-line',
    color: 'success',
    title: 'WhatsApp',
    value: '+62 838-9556-2263',
    action: `https://wa.me/${WHATSAPP_NUMBER}`,
    desc: 'Respon cepat di jam kerja',
  },
  {
    icon: 'ri-mail-send-line',
    color: 'primary',
    title: 'Email',
    value: SUPPORT_EMAIL,
    action: `mailto:${SUPPORT_EMAIL}`,
    desc: 'Balasan dalam 1x24 jam kerja',
  },
  {
    icon: 'ri-time-line',
    color: 'warning',
    title: 'Jam Operasional',
    value: 'Senin - Sabtu, 08.00 - 17.00 WIB',
    action: null,
    desc: 'Minggu & Hari Libur Nasional tutup',
  },
]

const form = ref({ name: '', businessName: '', message: '' })

const canSend = computed(() => !!(form.value.name && form.value.message))

const sendViaWhatsapp = () => {
  if (!canSend.value) return

  const text = `Halo Tim PintuKasir,%0A%0A` +
    `*Nama:* ${form.value.name}%0A` +
    `*Nama Usaha:* ${form.value.businessName || '-'}%0A%0A` +
    `*Pesan:*%0A${form.value.message}`

  window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, '_blank')
}
</script>

<template>
  <PublicPageShell>
    <PageHero
      eyebrow="Kontak"
      eyebrow-icon="ri-customer-service-2-line"
      title="Hubungi Kami"
      subtitle="Ada pertanyaan seputar fitur, harga, atau butuh bantuan? Tim kami siap membantu."
    />

    <section v-if="heroImageUrl" class="contact-hero-image-section pb-10">
      <VContainer>
        <VImg
          :src="heroImageUrl"
          rounded="xl"
          cover
          max-height="420"
          class="mx-auto contact-hero-image"
        />
      </VContainer>
    </section>

    <section class="contact-section pb-16">
      <VContainer>
        <VRow class="mb-10">
          <VCol
            v-for="method in contactMethods"
            :key="method.title"
            cols="12"
            md="4"
          >
            <VCard
              class="contact-card h-100"
              rounded="lg"
              variant="outlined"
              :href="method.action ?? undefined"
              :target="method.action ? '_blank' : undefined"
              :rel="method.action ? 'noopener noreferrer' : undefined"
            >
              <VCardText class="pa-6 text-center">
                <div
                  class="contact-icon-wrap mx-auto mb-4"
                  :class="`contact-icon-wrap--${method.color}`"
                >
                  <VIcon
                    :icon="method.icon"
                    size="24"
                    :color="method.color"
                  />
                </div>
                <h3 class="text-body-1 font-weight-bold mb-1">
                  {{ method.title }}
                </h3>
                <p class="text-body-2 font-weight-medium mb-1">
                  {{ method.value }}
                </p>
                <p class="text-caption text-medium-emphasis mb-0">
                  {{ method.desc }}
                </p>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>

        <VRow justify="center">
          <VCol
            cols="12"
            md="7"
            lg="6"
          >
            <VCard
              rounded="xl"
              variant="outlined"
            >
              <VCardText class="pa-6 pa-md-8">
                <h2 class="text-h6 font-weight-bold mb-1">
                  Kirim Pesan Cepat
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-6">
                  Pesan akan dikirim lewat WhatsApp ke tim kami dengan isian yang sudah terisi otomatis.
                </p>

                <VForm @submit.prevent="sendViaWhatsapp">
                  <VTextField
                    v-model="form.name"
                    label="Nama *"
                    class="mb-4"
                  />
                  <VTextField
                    v-model="form.businessName"
                    label="Nama Usaha (opsional)"
                    class="mb-4"
                  />
                  <VTextarea
                    v-model="form.message"
                    label="Pesan *"
                    rows="4"
                    class="mb-4"
                  />
                  <VBtn
                    color="success"
                    block
                    size="large"
                    prepend-icon="ri-whatsapp-line"
                    :disabled="!canSend"
                    @click="sendViaWhatsapp"
                  >
                    Kirim via WhatsApp
                  </VBtn>
                </VForm>
              </VCardText>
            </VCard>
          </VCol>
        </VRow>
      </VContainer>
    </section>
  </PublicPageShell>
</template>

<style lang="scss" scoped>
.contact-hero-image {
  max-inline-size: 960px;
}

.contact-card {
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 8px 24px rgba(var(--v-shadow-key-umbra-color), 12%) !important;
    transform: translateY(-2px);
  }
}

.contact-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;

  &--primary { background: rgba(var(--v-theme-primary), 0.12); }
  &--success { background: rgba(var(--v-theme-success), 0.12); }
  &--warning { background: rgba(var(--v-theme-warning), 0.12); }
}
</style>
